// The two pieces of 02-DESIGN/03-console/02-forms-from-schemas.md, drawn
// with the console's own components: the shape editor, which writes a
// schema as a list of fields, and the schema form, which draws a value under
// one. Both work on the shape of ./shape.ts and keep whatever it kept.

import { ArrowDown, ArrowUp, Ellipsis, Plus, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Button, Field as FieldBox, Select, TextArea, TextInput, when } from "../components.tsx";
import {
  ITEM_KINDS,
  KIND_WORDS,
  blankField,
  blankNode,
  kept,
  optionsOf,
  schemaOfNode,
  shapeOf,
  type Field,
  type Kind,
  type Node,
  type Problem,
} from "./shape.ts";

const FIELD_KINDS: readonly Kind[] = [...ITEM_KINDS, "list", "json"];

/** changeKind is a node turned into another kind: its words stay; into JSON it is written out; out of JSON it starts blank. */
function changeKind<T extends Node>(n: T, kind: Kind): T {
  if (kind === n.kind) {
    return n;
  }
  if (kind === "json") {
    return { ...n, ...blankNode("json"), json: JSON.stringify(schemaOfNode(n), null, 2) };
  }
  if (n.kind === "json") {
    return { ...n, ...blankNode(kind) };
  }
  const options = kind === "choice" ? n.options : [];
  return { ...n, ...blankNode(kind), options, extra: n.extra };
}

/**
 * ShapeEditor is a list of fields as rows — name, label, kind, required —
 * each with its details behind ⋯, a group's fields indented under it, and
 * Add a field at the foot. Problems are said beside the row they are about.
 */
export function ShapeEditor({
  fields,
  onChange,
  problems,
  depth = 0,
}: {
  fields: Field[];
  onChange: (fields: Field[]) => void;
  problems: readonly Problem[];
  depth?: number;
}) {
  const set = (i: number, f: Field) => {
    onChange(fields.map((x, j) => (j === i ? f : x)));
  };
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= fields.length) {
      return;
    }
    const out = [...fields];
    const [f] = out.splice(i, 1);
    if (f !== undefined) {
      out.splice(j, 0, f);
    }
    onChange(out);
  };
  return (
    <div className={depth === 0 ? "shape" : "shape nested"}>
      {depth === 0 && fields.length > 0 && (
        <div className="shape-row shape-head" aria-hidden="true">
          <span>Name</span>
          <span>Label</span>
          <span>Kind</span>
          <span>Required</span>
          <span />
        </div>
      )}
      {fields.map((f, i) => (
        <FieldRow
          key={f.id}
          f={f}
          first={i === 0}
          last={i === fields.length - 1}
          depth={depth}
          problems={problems}
          onChange={(nf) => {
            set(i, nf);
          }}
          onMove={(by) => {
            move(i, by);
          }}
          onRemove={() => {
            onChange(fields.filter((_, j) => j !== i));
          }}
        />
      ))}
      <div>
        <Button
          size="sm"
          kind="quiet"
          onClick={() => {
            onChange([...fields, blankField()]);
          }}
        >
          <Plus strokeWidth={1.8} aria-hidden="true" />
          Add a field
        </Button>
      </div>
    </div>
  );
}

function FieldRow({
  f,
  first,
  last,
  depth,
  problems,
  onChange,
  onMove,
  onRemove,
}: {
  f: Field;
  first: boolean;
  last: boolean;
  depth: number;
  problems: readonly Problem[];
  onChange: (f: Field) => void;
  onMove: (by: number) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(f.name === "");
  const said = problems.filter((p) => p.id === f.id);
  const of = f.of ?? blankNode("text");
  const nestedGroup = f.kind === "group" ? f : f.kind === "list" && of.kind === "group" ? of : undefined;
  return (
    <div className="shape-field">
      <div className="shape-row">
        <input
          className="input mono"
          aria-label="Name"
          value={f.name}
          placeholder="title"
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => {
            onChange({ ...f, name: e.target.value });
          }}
        />
        <input
          className="input"
          aria-label="Label"
          value={f.label}
          placeholder="Title"
          autoComplete="off"
          onChange={(e) => {
            onChange({ ...f, label: e.target.value });
          }}
        />
        <select
          className="input"
          aria-label="Kind"
          value={f.kind}
          onChange={(e) => {
            onChange(changeKind(f, e.target.value as Kind));
          }}
        >
          {FIELD_KINDS.map((k) => (
            <option key={k} value={k}>
              {KIND_WORDS[k]}
            </option>
          ))}
        </select>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={f.required}
            onChange={(e) => {
              onChange({ ...f, required: e.target.checked });
            }}
          />
          <span className="sr-only">Required</span>
        </label>
        <Button
          kind="icon"
          label={
            open ? `Hide the details of ${f.name || "this field"}` : `Details of ${f.name || "this field"}`
          }
          onClick={() => {
            setOpen(!open);
          }}
        >
          <Ellipsis size={16} strokeWidth={1.6} />
        </Button>
      </div>
      {said.map((p) => (
        <p key={p.what} className="small bad">
          {p.what}
        </p>
      ))}
      {open && (
        <div className="shape-details stack">
          {f.kind !== "json" && (
            <FieldBox label="Description" hint="Shown under the field wherever an item is drawn.">
              <TextInput
                value={f.description}
                onChange={(description) => {
                  onChange({ ...f, description });
                }}
              />
            </FieldBox>
          )}
          {f.kind === "list" && (
            <FieldBox label="Each item is">
              <Select
                value={of.kind}
                onChange={(k) => {
                  onChange({ ...f, of: changeKind(of, k) });
                }}
                options={ITEM_KINDS.map((k) => ({ value: k, label: KIND_WORDS[k] }))}
              />
            </FieldBox>
          )}
          <NodeDetails
            n={f.kind === "list" ? of : f}
            onChange={(n) => {
              onChange(f.kind === "list" ? { ...f, of: n } : { ...f, ...n });
            }}
          />
          <div className="row">
            <Button
              size="sm"
              kind="quiet"
              disabled={first}
              onClick={() => {
                onMove(-1);
              }}
            >
              <ArrowUp strokeWidth={1.8} aria-hidden="true" />
              Move up
            </Button>
            <Button
              size="sm"
              kind="quiet"
              disabled={last}
              onClick={() => {
                onMove(1);
              }}
            >
              <ArrowDown strokeWidth={1.8} aria-hidden="true" />
              Move down
            </Button>
            <Button size="sm" kind="danger-quiet" onClick={onRemove}>
              <X strokeWidth={1.8} aria-hidden="true" />
              Remove
            </Button>
          </div>
        </div>
      )}
      {nestedGroup !== undefined && (
        <ShapeEditor
          fields={nestedGroup.fields}
          depth={depth + 1}
          problems={problems}
          onChange={(fields) => {
            onChange(f.kind === "group" ? { ...f, fields } : { ...f, of: { ...of, fields } });
          }}
        />
      )}
    </div>
  );
}

/** NodeDetails are the parts of a value's kind behind ⋯: a choice's options, the default, the JSON, what else it checks. */
function NodeDetails({ n, onChange }: { n: Node; onChange: (n: Node) => void }) {
  const also = kept(n);
  return (
    <>
      {n.kind === "choice" && (
        <FieldBox label="Options" hint="One per line.">
          <TextArea
            rows={4}
            value={n.options.join("\n")}
            onChange={(t) => {
              onChange({ ...n, options: t.split("\n") });
            }}
          />
        </FieldBox>
      )}
      {n.kind === "boolean" && (
        <FieldBox label="Default">
          <Select
            value={n.default}
            onChange={(d) => {
              onChange({ ...n, default: d });
            }}
            options={[
              { value: "", label: "None" },
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
          />
        </FieldBox>
      )}
      {n.kind === "choice" && n.options.length > 0 && (
        <FieldBox label="Default">
          <Select
            value={n.default}
            onChange={(d) => {
              onChange({ ...n, default: d });
            }}
            options={[{ value: "", label: "None" }, ...optionsOf(n).map((o) => ({ value: o, label: o }))]}
          />
        </FieldBox>
      )}
      {(n.kind === "text" ||
        n.kind === "number" ||
        n.kind === "integer" ||
        n.kind === "date" ||
        n.kind === "datetime") && (
        <FieldBox label="Default">
          <TextInput
            value={n.default}
            onChange={(d) => {
              onChange({ ...n, default: d });
            }}
            placeholder={
              n.kind === "date" ? "2026-10-31" : n.kind === "datetime" ? "2026-10-31T09:00:00Z" : undefined
            }
          />
        </FieldBox>
      )}
      {n.kind === "json" && (
        <FieldBox
          label="Written as JSON"
          hint="The form cannot say this field; it is saved exactly as written."
        >
          <TextArea
            rows={6}
            mono
            value={n.json}
            onChange={(json) => {
              onChange({ ...n, json });
            }}
          />
        </FieldBox>
      )}
      {n.kind !== "json" && also.length > 0 && (
        <p className="small muted">
          Also checks: <span className="mono">{also.join(", ")}</span> — kept as written.
        </p>
      )}
    </>
  );
}

// ——— the schema form ———

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function Raw({ v }: { v: unknown }) {
  return <pre className="json one small">{JSON.stringify(v)}</pre>;
}

function matches(n: Node, v: unknown): boolean {
  switch (n.kind) {
    case "text":
    case "date":
    case "datetime":
      return typeof v === "string";
    case "choice":
      return typeof v === "string";
    case "number":
      return typeof v === "number";
    case "integer":
      return typeof v === "number" && Number.isInteger(v);
    case "boolean":
      return typeof v === "boolean";
    case "group":
      return isObj(v);
    case "list":
      return Array.isArray(v);
    default:
      return false;
  }
}

function Value({ n, v }: { n: Node; v: unknown }): ReactNode {
  if (v === undefined || v === null) {
    return <span className="faint">—</span>;
  }
  if (!matches(n, v)) {
    return <Raw v={v} />;
  }
  switch (n.kind) {
    case "boolean":
      return v === true ? "yes" : "no";
    case "number":
    case "integer":
      return (v as number).toLocaleString();
    case "datetime":
      return when(v as string);
    case "group":
      return <Fields fields={n.fields} value={v as Record<string, unknown>} />;
    case "list": {
      const items = v as unknown[];
      const of = n.of ?? blankNode("text");
      if (items.length === 0) {
        return <span className="faint">none</span>;
      }
      if (of.kind === "group") {
        const cols = {
          gridTemplateColumns: `repeat(${String(Math.max(of.fields.length, 1))}, minmax(0, 1fr))`,
        };
        return (
          <div className="table schema-table">
            <div className="tr th" style={cols}>
              {of.fields.map((f) => (
                <span key={f.id}>{f.label || <span className="mono">{f.name}</span>}</span>
              ))}
            </div>
            {items.map((it, i) => (
              <div className="tr" key={i} style={isObj(it) ? cols : undefined}>
                {isObj(it) ? (
                  of.fields.map((f) => (
                    <span key={f.id}>
                      {f.kind === "json" ? <Raw v={it[f.name]} /> : <Value n={f} v={it[f.name]} />}
                    </span>
                  ))
                ) : (
                  <Raw v={it} />
                )}
              </div>
            ))}
          </div>
        );
      }
      return (
        <span>
          {items.map((it, i) => (
            <span key={i}>
              {i > 0 && ", "}
              <Value n={of} v={it} />
            </span>
          ))}
        </span>
      );
    }
    default:
      return v as string;
  }
}

function Fields({ fields, value }: { fields: readonly Field[]; value: Record<string, unknown> }) {
  const named = new Set(fields.map((f) => f.name));
  const others = Object.entries(value).filter(([k]) => !named.has(k));
  return (
    <dl className="kv schema-view">
      {fields.map((f) => (
        <div key={f.id} className="contents">
          <dt>
            {f.label || <span className="mono">{f.name}</span>}
            {f.description !== "" && <span className="hint">{f.description}</span>}
          </dt>
          <dd>
            {f.kind === "json" ? (
              value[f.name] === undefined ? (
                <span className="faint">—</span>
              ) : (
                <Raw v={value[f.name]} />
              )
            ) : (
              <Value n={f} v={value[f.name]} />
            )}
          </dd>
        </div>
      ))}
      {others.map(([k, v]) => (
        <div key={k} className="contents">
          <dt>
            <span className="mono">{k}</span>
          </dt>
          <dd>
            <Raw v={v} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * SchemaView draws a value under a schema as its fields, labelled, read
 * only. It draws nothing — undefined — where the schema is not one the form
 * can read or the value is not a set of fields: the caller shows JSON.
 */
export function schemaView(schema: unknown, value: unknown): ReactNode | undefined {
  if (schema === undefined || !isObj(value)) {
    return undefined;
  }
  const shape = shapeOf(schema);
  if ("reason" in shape || shape.fields.length === 0) {
    return undefined;
  }
  return <Fields fields={shape.fields} value={value} />;
}
